import type { ApiError, AuthResponse, AuthTokens, PaginatedResponse, User, Org, Area } from '@goodchat/shared/types';

export interface ApiClientConfig {
  baseUrl: string;
  getAccessToken: () => string | null;
  getRefreshToken?: () => string | null;
  onTokenRefreshed?: (accessToken: string, refreshToken: string) => void;
  onUnauthorized?: () => void;
}

export class ApiClient {
  private config: ApiClientConfig;
  private refreshPromise: Promise<boolean> | null = null;

  constructor(config: ApiClientConfig) {
    this.config = config;
  }

  /** Try to refresh the access token using the refresh token. Returns true on success. */
  private async tryRefresh(): Promise<boolean> {
    // Deduplicate concurrent refresh attempts within this tab
    if (this.refreshPromise) return this.refreshPromise;

    this.refreshPromise = (async () => {
      const doRefresh = async (): Promise<boolean> => {
        // Re-read token from store (another tab may have refreshed already)
        const refreshToken = this.config.getRefreshToken?.();
        if (!refreshToken) return false;

        try {
          const response = await fetch(`${this.config.baseUrl}/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken }),
          });
          if (!response.ok) {
            // Check if another tab refreshed while we were waiting
            const currentToken = this.config.getRefreshToken?.();
            if (currentToken && currentToken !== refreshToken) {
              return true; // Another tab refreshed, use their tokens
            }
            return false;
          }
          const data = (await response.json()) as { accessToken: string; refreshToken: string };
          this.config.onTokenRefreshed?.(data.accessToken, data.refreshToken);
          return true;
        } catch {
          return false;
        }
      };

      // Use Web Locks API if available (browser environment) to coordinate across tabs
      if (typeof navigator !== 'undefined' && navigator.locks) {
        return navigator.locks.request('goodchat_token_refresh', doRefresh);
      }
      return doRefresh();
    })();

    try {
      return await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  private async request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const token = this.config.getAccessToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // 30-second request timeout
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 30_000);

    let response: Response;
    try {
      response = await fetch(`${this.config.baseUrl}${path}`, {
        ...options,
        headers,
        signal: controller.signal,
      });
    } finally {
      clearTimeout(timeout);
    }

    // On 401, attempt to refresh and retry once
    if (response.status === 401 && !path.startsWith('/auth/')) {
      const refreshed = await this.tryRefresh();
      if (refreshed) {
        // Retry with the new token
        const newToken = this.config.getAccessToken();
        if (newToken) {
          headers['Authorization'] = `Bearer ${newToken}`;
        }
        const retryResponse = await fetch(`${this.config.baseUrl}${path}`, {
          ...options,
          headers,
        });
        if (retryResponse.status === 401) {
          this.config.onUnauthorized?.();
          throw new ApiClientError('Unauthorized', 'AUTH_TOKEN_EXPIRED', 401);
        }
        if (!retryResponse.ok) {
          const error = (await retryResponse.json()) as ApiError;
          throw new ApiClientError(error.error, error.code, retryResponse.status, error.details);
        }
        if (retryResponse.status === 204) return undefined as T;
        return retryResponse.json() as Promise<T>;
      }

      this.config.onUnauthorized?.();
      throw new ApiClientError('Unauthorized', 'AUTH_TOKEN_EXPIRED', 401);
    }

    if (!response.ok) {
      const error = (await response.json()) as ApiError;
      throw new ApiClientError(error.error, error.code, response.status, error.details);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return response.json() as Promise<T>;
  }

  /**
   * Raw passthrough for callers with pre-built RequestInit (method + stringified
   * body) — gets the same timeout + 401-refresh-retry behavior as the typed
   * helpers. Used by the mobile app's apiFetch delegate.
   */
  async raw<T>(path: string, options: RequestInit = {}): Promise<T> {
    return this.request<T>(path, options);
  }

  /**
   * Single-flight token refresh for proactive callers (timers, tab-focus
   * handlers). ALL refreshes must go through here — a raw POST /auth/refresh
   * racing this client's internal refresh double-spends the rotating token,
   * which the server treats as token theft and revokes the whole session
   * family (i.e., logs the user out everywhere).
   */
  async refresh(): Promise<boolean> {
    return this.tryRefresh();
  }

  async get<T>(path: string): Promise<T> {
    return this.request<T>(path, { method: 'GET' });
  }

  async post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async patch<T>(path: string, body: unknown): Promise<T> {
    return this.request<T>(path, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  async put<T>(path: string, body: unknown): Promise<T> {
    return this.request<T>(path, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  async delete<T>(path: string): Promise<T> {
    return this.request<T>(path, { method: 'DELETE' });
  }

  // Health check
  async health(): Promise<{ status: string; timestamp: string }> {
    return this.get('/health');
  }
}

export class ApiClientError extends Error {
  code: string;
  status: number;
  details?: Record<string, unknown>;

  constructor(message: string, code: string, status: number, details?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiClientError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

export function createApiClient(config: ApiClientConfig): ApiClient {
  return new ApiClient(config);
}
